// frontend/src/utils/use-project-results.ts
import { useEffect, useState } from "react";
import i18n from "@/i18n";
import api from "@/utils/axios";
import { useProjectFormStoreOptional } from "@/store/project-form-provider";
//import type { ProjectModelResults } from "@/types/ProjectModelResults";

export interface YearlyValue {
  Year: number;
  [key: string]: number;
}

export interface ProjectModelResults {
  revenue: YearlyValue[];
  opex: YearlyValue[];
  interest: YearlyValue[];
  redemption: YearlyValue[];
  debt_service: YearlyValue[];
  remaining_debt: YearlyValue[];
  debt: YearlyValue[];
  production?: YearlyValue[];
  capex?: YearlyValue[];
  taxes?: YearlyValue[];
  cash_flows?: YearlyValue[];
  profit?: YearlyValue[];
  investment?: YearlyValue[];
  cashflow?: YearlyValue[];
  key_metrics?: Record<string, number>;
  [key: string]: any;
}

export function useProjectResults(projectId: string | undefined) {
  const store = useProjectFormStoreOptional();

  // Prefer provider-cached results when available
  const [results, setResults] = useState<ProjectModelResults | null>(store?.results ?? null);
  const [loading, setLoading] = useState<boolean>(store ? store.resultsLoading : true);
  const [error, setError] = useState<string | null>(store ? store.resultsError : null);

  // One refetch function, delegates to provider when present
  async function fetchResults() {
    if (store) {
      await store.fetchResults();
      // mirror latest provider state
      setResults(store.results);
      setLoading(store.resultsLoading);
      setError(store.resultsError);
      return;
    }
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await api.post(`/api/projects/${projectId}/calculate`);
      setResults(res.data);
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || i18n.t("project.form.failed_to_load_results");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (store) {
      // Sync local state with provider cache so Results tab switches are instant
      setResults(store.results);
      setLoading(store.resultsLoading);
      setError(store.resultsError);
      return;
    }

    // Fallback behavior (no provider): fetch and listen for saves
    fetchResults();
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ projectId?: string | number }>;
      if (!ev?.detail?.projectId) return;
      if (String(ev.detail.projectId) === String(projectId)) {
        fetchResults();
      }
    };
    document.addEventListener("project:saved", handler as EventListener);
    return () => document.removeEventListener("project:saved", handler as EventListener);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, store, store?.results, store?.resultsLoading, store?.resultsError]);

  return { results, loading, error, refetch: fetchResults };
}
